import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { messages } from './messages';
import { FormattedCurrency } from '../../../../../../../components/formatted-currency';
import { Text, TextType } from 'ui-components/components/text';
import { ShipmentTierMarker } from './shipment-tier-marker/shipment-tier-marker';

import styles from './shipment-limit-option-content.module.css';
import sharedStyle from '../plan-details.module.css';

/**
 * @typedef {import('../../../../../../../prop-types/subscription-data-types.d.ts').MonetaryValue} MonetaryValue
 */

/**
 * @param {object} props
 * @param {MonetaryValue} props.cost
 * @param {boolean} props.shouldShowIncludedForZeroCost
 * @returns {React.JSX.Element}
 */
const ShipmentLimitCost = ({ cost, shouldShowIncludedForZeroCost }) => {
  if (cost?.value) {
    return (
      <FormattedMessage
        {...messages.shipmentLimitAdditionalCost}
        values={{
          cost: <FormattedCurrency currencyCode={cost?.code} value={cost.value} />,
        }}
      />
    );
  }

  if (shouldShowIncludedForZeroCost) {
    return <FormattedMessage {...messages.includedAbbreviation} />;
  }

  return <FormattedCurrency currencyCode={cost?.code} fractionDigits={0} value={0} />;
};

/**
 * @param {object} props
 * @param {number} props.shipmentLimitCount
 * @param {MonetaryValue} props.shipmentLimitCost
 * @param {boolean} props.shouldShowIncludedForZeroCost
 * @param {number | null} [props.discountedPrice]
 * @param {number | null} [props.discountPercent]
 * @param {string | null} [props.discountType]
 * @returns {React.JSX.Element}
 */
export const ShipmentLimitOptionContent = ({
  shipmentLimitCount,
  shipmentLimitCost,
  shouldShowIncludedForZeroCost,
  discountedPrice = null,
  discountPercent = null,
  discountType = null,
}) => {
  if (discountedPrice != null) {
    return (
      <div className={styles['shipment-limit-dropdown-item-with-discount']}>
        <span className={styles['shipment-limit-item-volume-and-marker']}>
          <Text element='span' textType={TextType.Paragraph} value={<FormattedNumber value={shipmentLimitCount} />} />
          {discountType && <ShipmentTierMarker discountPercent={discountPercent} variant={discountType} />}
        </span>
        <span className={styles['shipment-limit-item-discounted-price']}>
          <span className={styles['shipment-limit-item-strikethrough']}>
            <FormattedCurrency currencyCode={shipmentLimitCost?.code} value={shipmentLimitCost?.value} />
          </span>
          &nbsp;
          <FormattedCurrency currencyCode={shipmentLimitCost?.code} value={discountedPrice} />
        </span>
      </div>
    );
  }

  return (
    <>
      <Text
        className={styles['shipment-limit-option-count']}
        element='span'
        textType={TextType.Paragraph}
        value={<FormattedNumber value={shipmentLimitCount} />}
      />
      <Text
        className={sharedStyle['plan-option-content-cost']}
        element='span'
        textType={TextType.Caption}
        value={<ShipmentLimitCost cost={shipmentLimitCost} shouldShowIncludedForZeroCost={shouldShowIncludedForZeroCost} />}
      />
    </>
  );
};
